import { allCountries, type CountryData } from 'country-region-data';

import { normaliseLocale } from '../contract/defaults';
import { REGIONAL_DEFAULTS } from '../contract/regional';

const localizedCountryName = (code: string, locale: string): string => {
  const resolvedLocale = normaliseLocale(locale) ?? REGIONAL_DEFAULTS.locale;
  try {
    return (
      new Intl.DisplayNames([resolvedLocale], { type: 'region' }).of(code) ??
      code
    );
  } catch {
    return code;
  }
};

type RegionTuple = readonly [string, string];

let regionIndex: ReadonlyMap<string, readonly RegionTuple[]> | null = null;

const ensureRegionIndex = (): ReadonlyMap<string, readonly RegionTuple[]> => {
  if (!regionIndex) {
    const map = new Map<string, readonly RegionTuple[]>();
    for (const entry of allCountries as CountryData[]) {
      const [, code, regions] = entry;
      map.set(code, regions);
    }
    regionIndex = map;
  }
  return regionIndex;
};

/**
 * Region tuples for any ISO code present in `country-region-data`.
 */
export const getRegionsForCountryCode = (
  country: string
): readonly RegionTuple[] => ensureRegionIndex().get(country) ?? [];

/**
 * Region short codes for a country code in the full dataset.
 */
export const getRegionCodesForCountryCode = (
  country: string
): readonly string[] =>
  getRegionsForCountryCode(country).map(([, shortCode]) => shortCode);

export type GlobalAddressCountryOption = {
  readonly code: string;
  readonly name: string;
};

/**
 * True when `code` exists in the global country dataset (ISO 3166-1 alpha-2).
 */
export const isGlobalAddressCountryCode = (code: string): boolean =>
  ensureRegionIndex().has(code);

/**
 * Every country in `country-region-data`, sorted by localized name.
 */
export const allAddressCountries = (
  locale: string = 'en-US'
): readonly GlobalAddressCountryOption[] => {
  const index = ensureRegionIndex();
  const sortLocale = normaliseLocale(locale) ?? REGIONAL_DEFAULTS.locale;
  const withNames = [...index.keys()].map((code) => ({
    code,
    name: localizedCountryName(code, locale),
  }));
  return withNames
    .sort((a, b) => {
      try {
        return a.name.localeCompare(b.name, sortLocale);
      } catch {
        return a.name.localeCompare(b.name);
      }
    })
    .map(({ code, name }) => ({ code, name }));
};
